import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  CloudUpload as ReplaceIcon,
  NoteAdd as AdditionalIcon,
  DeleteOutline as ClearIcon,
} from '@mui/icons-material';
import { agreementService, SignedAgreement } from '../services/agreementService';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';

const LegalSection: React.FC = () => {
  const { user } = useAuth();
  const [agreements, setAgreements] = useState<SignedAgreement[]>([]);
  const [filteredAgreements, setFilteredAgreements] = useState<SignedAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceDialog, setReplaceDialog] = useState<{ open: boolean; agreement: SignedAgreement | null; file: File | null; submitting: boolean }>({
    open: false,
    agreement: null,
    file: null,
    submitting: false,
  });
  const [additionalDialog, setAdditionalDialog] = useState<{
    open: boolean;
    agreement: SignedAgreement | null;
    pdfFile: File | null;
    additionalNotes: string;
    submitting: boolean;
  }>({
    open: false,
    agreement: null,
    pdfFile: null,
    additionalNotes: '',
    submitting: false,
  });

  useEffect(() => {
    fetchAgreements();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAgreements(agreements);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = agreements.filter(
        (agreement) =>
          agreement.user_name?.toLowerCase().includes(term) ||
          agreement.user_email?.toLowerCase().includes(term) ||
          agreement.agreement_name?.toLowerCase().includes(term) ||
          agreement.username?.toLowerCase().includes(term)
      );
      setFilteredAgreements(filtered);
    }
  }, [searchTerm, agreements]);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await agreementService.getAllSignedAgreements();
      setAgreements(data);
      setFilteredAgreements(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch signed agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (userAgreementId: number) => {
    try {
      await agreementService.downloadAgreementPdf(userAgreementId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to download PDF');
    }
  };

  const handleView = async (userAgreementId: number) => {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/agreements/download/${userAgreementId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err: any) {
      alert('Failed to view PDF');
    }
  };

  const openReplaceDialog = (agreement: SignedAgreement) => {
    setReplaceDialog({ open: true, agreement, file: null, submitting: false });
  };
  const closeReplaceDialog = () => {
    setReplaceDialog({ open: false, agreement: null, file: null, submitting: false });
  };
  const handleReplaceSubmit = async () => {
    if (!replaceDialog.agreement || !replaceDialog.file) return;
    setReplaceDialog((p) => ({ ...p, submitting: true }));
    try {
      await agreementService.replaceCustomPdf(replaceDialog.agreement.id, replaceDialog.file);
      await fetchAgreements();
      closeReplaceDialog();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to replace PDF');
    } finally {
      setReplaceDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const openAdditionalDialog = (agreement: SignedAgreement) => {
    setAdditionalDialog({
      open: true,
      agreement,
      pdfFile: null,
      additionalNotes: agreement.additional_notes || '',
      submitting: false,
    });
  };
  const closeAdditionalDialog = () => {
    setAdditionalDialog({
      open: false,
      agreement: null,
      pdfFile: null,
      additionalNotes: '',
      submitting: false,
    });
  };
  const handleAdditionalSubmit = async () => {
    if (!additionalDialog.agreement) return;
    if (!additionalDialog.pdfFile && additionalDialog.additionalNotes === (additionalDialog.agreement.additional_notes || '')) {
      alert('Add a PDF and/or change the notes to save.');
      return;
    }
    setAdditionalDialog((p) => ({ ...p, submitting: true }));
    try {
      await agreementService.uploadAdditionalAgreement(additionalDialog.agreement.id, {
        pdf: additionalDialog.pdfFile || undefined,
        additional_notes: additionalDialog.additionalNotes,
      });
      await fetchAgreements();
      closeAdditionalDialog();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save additional agreement');
    } finally {
      setAdditionalDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const handleDownloadAdditional = async (userAgreementId: number) => {
    try {
      await agreementService.downloadAdditionalPdf(userAgreementId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to download additional PDF');
    }
  };

  const handleClearCustomPdf = async (agreement: SignedAgreement) => {
    if (!window.confirm('Remove custom PDF and revert to original signed agreement?')) return;
    try {
      await agreementService.clearCustomPdf(agreement.id);
      await fetchAgreements();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to clear custom PDF');
    }
  };

  const handleClearAdditional = async (agreement: SignedAgreement) => {
    if (!window.confirm('Remove additional PDF and notes for this agreement?')) return;
    try {
      await agreementService.clearAdditionalAgreement(agreement.id);
      await fetchAgreements();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to clear additional data');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'pmp_admin');
  };

  if (!isPmpAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. This section is only available to PMP Admins.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Legal Section - Signed Agreements
        </Typography>
        <Chip 
          label={`${filteredAgreements.length} Agreement${filteredAgreements.length !== 1 ? 's' : ''}`} 
          color="primary" 
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by user name, email, or agreement name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>User Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Agreement Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Version</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Signed Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgreements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {searchTerm ? 'No agreements found matching your search.' : 'No signed agreements found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAgreements.map((agreement) => (
                <TableRow key={agreement.id} hover>
                  <TableCell>{agreement.user_name || 'N/A'}</TableCell>
                  <TableCell>{agreement.user_email || 'N/A'}</TableCell>
                  <TableCell>{agreement.agreement_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`v${agreement.agreement_version}`} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(agreement.signed_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {agreement.has_pdf ? (
                        <Chip label="Signed" color="success" size="small" />
                      ) : (
                        <Chip label="No PDF" color="default" size="small" />
                      )}
                      {agreement.has_custom_pdf && (
                        <Chip label="Custom PDF" size="small" variant="outlined" color="info" />
                      )}
                      {(agreement.has_additional_pdf || agreement.has_additional_notes) && (
                        <Chip label="Additional" size="small" variant="outlined" color="secondary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {agreement.has_pdf && (
                        <>
                          <Tooltip title="View PDF">
                            <IconButton color="primary" size="small" onClick={() => handleView(agreement.id)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton color="secondary" size="small" onClick={() => handleDownload(agreement.id)}>
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Replace with custom PDF">
                        <IconButton size="small" onClick={() => openReplaceDialog(agreement)} color="info">
                          <ReplaceIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add additional agreement / notes">
                        <IconButton size="small" onClick={() => openAdditionalDialog(agreement)} color="secondary">
                          <AdditionalIcon />
                        </IconButton>
                      </Tooltip>
                      {agreement.has_additional_pdf && (
                        <Tooltip title="Download additional PDF">
                          <IconButton size="small" onClick={() => handleDownloadAdditional(agreement.id)}>
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {agreement.has_custom_pdf && (
                        <Tooltip title="Remove custom PDF">
                          <IconButton size="small" onClick={() => handleClearCustomPdf(agreement)} color="error">
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(agreement.has_additional_pdf || agreement.has_additional_notes) && (
                        <Tooltip title="Clear additional data">
                          <IconButton size="small" onClick={() => handleClearAdditional(agreement)} color="error">
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={replaceDialog.open} onClose={closeReplaceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Replace with custom PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a PDF to replace the signed agreement for this user. View/Download will show your custom PDF instead of the original.
          </Typography>
          <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>
            {replaceDialog.file ? replaceDialog.file.name : 'Choose PDF file'}
            <input
              type="file"
              hidden
              accept=".pdf,application/pdf"
              onChange={(e) => setReplaceDialog((p) => ({ ...p, file: e.target.files?.[0] || null }))}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReplaceDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleReplaceSubmit} disabled={!replaceDialog.file || replaceDialog.submitting}>
            {replaceDialog.submitting ? 'Uploading…' : 'Replace PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={additionalDialog.open} onClose={closeAdditionalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add additional agreement data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Attach an additional PDF (e.g. addendum) and/or notes for this user&apos;s agreement.
          </Typography>
          <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
            {additionalDialog.pdfFile ? additionalDialog.pdfFile.name : 'Optional: Choose additional PDF'}
            <input
              type="file"
              hidden
              accept=".pdf,application/pdf"
              onChange={(e) => setAdditionalDialog((p) => ({ ...p, pdfFile: e.target.files?.[0] || null }))}
            />
          </Button>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Additional notes"
            placeholder="Optional notes or agreement data for this user..."
            value={additionalDialog.additionalNotes}
            onChange={(e) => setAdditionalDialog((p) => ({ ...p, additionalNotes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdditionalDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdditionalSubmit}
            disabled={additionalDialog.submitting}
          >
            {additionalDialog.submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">
          This section displays all user agreements signed across the platform.
          You can view and download signed agreement PDFs, replace a user&apos;s agreement with a custom PDF, or add additional agreement data (PDF + notes).
        </Typography>
      </Box>
    </Box>
  );
};

export default LegalSection;

